/** Device incidents, the repair history that follows a device, and damage invoices. */
import { apiUrl, upload } from './api';
import type { AssetStatus, PersonSummary, WorkOrderStatus } from './types';

export type IncidentType = 'ACCIDENTAL_DAMAGE' | 'INTENTIONAL_DAMAGE' | 'LIQUID_DAMAGE' | 'LOST' | 'STOLEN' | 'MALFUNCTION' | 'OTHER';
export type IncidentStatus = 'OPEN' | 'IN_REPAIR' | 'REPAIRED' | 'REPLACED' | 'CLOSED' | 'DISMISSED';
export type DeviceRepairKind = 'REPAIR' | 'VENDOR_REPAIR' | 'WARRANTY_REPAIR' | 'REPLACEMENT' | 'INSPECTION' | 'NOTE';
export type DamageInvoiceStatus = 'DRAFT' | 'SENT' | 'PAID' | 'WAIVED' | 'VOID';

type Tone = 'gray' | 'green' | 'blue' | 'amber' | 'red' | 'violet' | 'brand' | 'slate';

export const MAX_PHOTOS = 20;

export const INCIDENT_TYPE_LABEL: Record<IncidentType, string> = {
  ACCIDENTAL_DAMAGE: 'Accidental damage',
  INTENTIONAL_DAMAGE: 'Intentional damage',
  LIQUID_DAMAGE: 'Liquid damage',
  LOST: 'Lost',
  STOLEN: 'Stolen',
  MALFUNCTION: 'Malfunction',
  OTHER: 'Other',
};

export const INCIDENT_STATUS_LABEL: Record<IncidentStatus, string> = {
  OPEN: 'Open',
  IN_REPAIR: 'In repair',
  REPAIRED: 'Repaired',
  REPLACED: 'Replaced',
  CLOSED: 'Closed',
  DISMISSED: 'Dismissed',
};

export const INCIDENT_STATUS_TONE: Record<IncidentStatus, Tone> = {
  OPEN: 'amber',
  IN_REPAIR: 'blue',
  REPAIRED: 'green',
  REPLACED: 'violet',
  CLOSED: 'gray',
  DISMISSED: 'gray',
};

export const REPAIR_KIND_LABEL: Record<DeviceRepairKind, string> = {
  REPAIR: 'Repair',
  VENDOR_REPAIR: 'Vendor repair',
  WARRANTY_REPAIR: 'Warranty repair',
  REPLACEMENT: 'Replacement',
  INSPECTION: 'Inspection',
  NOTE: 'Note',
};

export const REPAIR_KIND_TONE: Record<DeviceRepairKind, Tone> = {
  REPAIR: 'brand',
  VENDOR_REPAIR: 'blue',
  WARRANTY_REPAIR: 'green',
  REPLACEMENT: 'violet',
  INSPECTION: 'gray',
  NOTE: 'gray',
};

export const INVOICE_STATUS_LABEL: Record<DamageInvoiceStatus, string> = { DRAFT: 'Draft', SENT: 'Sent', PAID: 'Paid', WAIVED: 'Waived', VOID: 'Void' };

export const INVOICE_STATUS_TONE: Record<DamageInvoiceStatus, Tone> = { DRAFT: 'gray', SENT: 'amber', PAID: 'green', WAIVED: 'blue', VOID: 'red' };

export interface IncidentAsset {
  id: string;
  assetTag: string;
  name: string;
  serialNumber: string | null;
  status: AssetStatus;
}

export interface IncidentInvoiceSummary {
  id: string;
  number: number;
  status: DamageInvoiceStatus;
  total: number;
}

export interface IncidentSummary {
  id: string;
  number: number;
  type: IncidentType;
  status: IncidentStatus;
  occurredAt: string;
  description: string;
  statusChangedAt: string;
  createdByName: string;
  createdAt: string;
  asset: IncidentAsset;
  /** Who had the device; damage invoices bill their family. */
  responsiblePerson: PersonSummary | null;
  workOrder: { id: string; number: number; title: string; status: WorkOrderStatus } | null;
  photoCount: number;
  invoices: IncidentInvoiceSummary[];
}

export interface IncidentPhoto {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedByName: string;
  createdAt: string;
  canDelete: boolean;
}

export interface DeviceRepair {
  id: string;
  kind: DeviceRepairKind;
  /** YYYY-MM-DD */
  performedOn: string;
  summary: string;
  details: string | null;
  cost: number | null;
  vendor: string | null;
  createdByName: string;
  createdAt: string;
  incident: { id: string; number: number } | null;
  workOrder: { id: string; number: number } | null;
  canEdit: boolean;
}

export interface IncidentDetail extends Omit<IncidentSummary, 'asset'> {
  asset: IncidentAsset & { manufacturer: string | null; model: string | null; purchasePrice: number | null };
  photos: IncidentPhoto[];
  repairs: DeviceRepair[];
  history: { id: string; action: string; summary: string; actorName: string | null; createdAt: string }[];
  permissions: {
    update: boolean;
    /** The statuses this person can give it; closing and dismissing are for administrators. */
    statuses: IncidentStatus[];
    addPhotos: boolean;
    createInvoice: boolean;
  };
}

export interface DeviceHistory {
  asset: IncidentAsset;
  incidents: IncidentSummary[];
  repairs: DeviceRepair[];
  totals: { incidents: number; activeIncidents: number; repairs: number; repairCost: number; billed: number; paid: number };
}

export interface IncidentContext {
  asset: IncidentAsset;
  suggestedPerson: PersonSummary | null;
  suggestion: 'CHECKED_OUT' | 'LAST_CHECKOUT' | null;
}

export interface InvoiceItem {
  description: string;
  quantity: number;
  unitAmount: number;
}

export interface DamageInvoiceSummary {
  id: string;
  number: number;
  status: DamageInvoiceStatus;
  studentName: string;
  billToName: string | null;
  /** YYYY-MM-DD */
  issuedOn: string;
  /** YYYY-MM-DD */
  dueOn: string;
  emailedAt: string | null;
  blackbaudPostedAt: string | null;
  paidAt: string | null;
  closedAt: string | null;
  createdAt: string;
  total: number;
  overdue: boolean;
  incident: { id: string; number: number; type: IncidentType; asset: { id: string; assetTag: string; name: string } };
}

export interface DamageInvoice extends Omit<DamageInvoiceSummary, 'incident'> {
  billToEmails: string[];
  notes: string | null;
  emailedTo: string[];
  blackbaudBatchId: string | null;
  blackbaudImportId: string | null;
  closedReason: string | null;
  createdByName: string;
  updatedAt: string;
  items: (InvoiceItem & { id: string })[];
  incident: {
    id: string;
    number: number;
    type: IncidentType;
    status: IncidentStatus;
    occurredAt: string;
    description: string;
    asset: { id: string; assetTag: string; name: string; serialNumber: string | null; manufacturer: string | null; model: string | null };
  };
  person: PersonSummary | null;
  school: { name: string; contactEmail: string | null; paymentInstructions: string | null; logoUrl: string | null };
  email: { ready: boolean; mailbox: string | null; reason: string | null };
  blackbaud: { ready: boolean; demo: boolean; reason: string | null };
  permissions: { edit: boolean; delete: boolean; email: boolean; postToBlackbaud: boolean; markPaid: boolean; waive: boolean; void: boolean; reopen: boolean };
  /** After emailing: the addresses it couldn't go to. */
  failed?: string[];
}

export interface Guardian {
  name: string;
  email: string | null;
  relationship: string | null;
  /** Responsible for tuition in Blackbaud. */
  billing: boolean;
}

export interface GuardianLookup {
  available: boolean;
  reason: string | null;
  guardians: Guardian[];
}

export const incidentPhotoUrl = (incidentId: string, photoId: string) => apiUrl(`/incidents/${incidentId}/photos/${photoId}`);

export const lineTotal = (items: Pick<InvoiceItem, 'quantity' | 'unitAmount'>[]) =>
  Math.round(items.reduce((sum, item) => sum + item.quantity * item.unitAmount, 0) * 100) / 100;

/** Uploads a photo to an incident, or on its own for a report that isn't saved yet. */
export function uploadIncidentPhoto(photo: Blob, fileName: string, incidentId?: string) {
  return upload<IncidentPhoto>(incidentId ? `/incidents/${incidentId}/photos` : '/incidents/photos', photo, {
    'x-photo': encodeURIComponent(JSON.stringify({ fileName })),
  });
}
